import { WeblabedFeatures } from 'src/commons/constants';
import { logger } from './logger';
import { KatalMetricsPublisher } from './metrics';

// redirect_to=none tells enforce-midway to return 200 after auth instead of
// redirecting to the landing page — the hidden iframe only needs the fresh
// token from the set-cookie header.
const MIDWAY_REFRESH_ENDPOINT =
  '/common/ads/enforce-midway?refresh=true&redirect_to=none';

// Midway session is valid for 60 min; refresh proactively at 15 min
const MIDWAY_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const MIDWAY_REFRESH_SETTLE_TIMEOUT_MS = 60 * 1000; // 60 sec
const MIDWAY_REFRESH_MAX_ATTEMPTS = 2; // 1 retry
const MIDWAY_REFRESH_ON_INIT = true;

// __AACChrome__ context is populated asynchronously after init; poll for it
// rather than assume a fixed delay.
const MIDWAY_CONTEXT_POLL_INTERVAL_MS = 500;
const MIDWAY_CONTEXT_POLL_TIMEOUT_MS = 30 * 1000; // give up after 30 sec

const MidwayRefreshMetricsPublisher =
  KatalMetricsPublisher.newChildActionPublisherForMethod('MidwayRefresh');

// bare window under module federation points to webpack Module instance instead of Window
const win = globalThis as unknown as Window;

const getContext = () => win.__AACChrome__?.context;

const isFeatureEnabled = (featureName: string): boolean =>
  getContext()?.featureLaunchInfo?.features?.some(
    (f) => f.name === featureName && f.isEnabled,
  ) ?? false;

const loadHiddenIframe = (url: string, timeoutMs: number): Promise<void> =>
  new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');

    // 1px avoids browsers skipping load for zero-size iframes
    iframe.style.cssText =
      'width:1px;height:1px;position:absolute;visibility:hidden';
    iframe.src = url;

    const cleanup = () => {
      clearTimeout(timer);
      iframe.remove();
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`Hidden iframe timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    iframe.onload = () => {
      cleanup();
      resolve();
    };

    iframe.onerror = (event) => {
      cleanup();
      const message =
        event instanceof ErrorEvent ? event.message : 'Unknown error';
      reject(new Error(`Hidden iframe failed to load: ${message}`));
    };

    document.body.appendChild(iframe);
  });

const stopwatch = () => {
  const start = performance.now();
  return () => performance.now() - start;
};

const refreshMidwaySession = async () => {
  const elapsed = stopwatch();

  let lastError: Error | undefined;
  let attempt = 1;

  while (attempt <= MIDWAY_REFRESH_MAX_ATTEMPTS) {
    try {
      await loadHiddenIframe(
        MIDWAY_REFRESH_ENDPOINT,
        MIDWAY_REFRESH_SETTLE_TIMEOUT_MS,
      );
      lastError = undefined;
      break;
    } catch (error) {
      lastError = error as Error;
      attempt++;
    }
  }

  MidwayRefreshMetricsPublisher.publishCounterMonitor('Count', 1);
  if (lastError) {
    logger.error('Midway session refresh failed after retries:', lastError);
    MidwayRefreshMetricsPublisher.publishCounterMonitor('Error', 1);
    MidwayRefreshMetricsPublisher.publishTimerMonitor(
      'ErrorLatency',
      elapsed(),
    );
  } else {
    MidwayRefreshMetricsPublisher.publishCounterMonitor('Error', 0);
    MidwayRefreshMetricsPublisher.publishCounterMonitor('Attempts', attempt);
    MidwayRefreshMetricsPublisher.publishTimerMonitor(
      'SuccessLatency',
      elapsed(),
    );
  }
};

const startMidwaySessionRefresh = () => {
  if (!getContext()?.isInternalUser) return;
  if (!isFeatureEnabled(WeblabedFeatures.ADS_PORTAL_MIDWAY_REFRESH)) return;

  if (MIDWAY_REFRESH_ON_INIT) {
    refreshMidwaySession();
  }
  setInterval(refreshMidwaySession, MIDWAY_REFRESH_INTERVAL_MS);
};

// Wait for __AACChrome__ context to be populated, then start. Bails out after
// the timeout so a never-ready context can't leave a dangling interval.
const whenContextReady = (onReady: () => void) => {
  const elapsed = stopwatch();

  if (getContext()) {
    MidwayRefreshMetricsPublisher.publishCounterMonitor('ContextTimeout', 0);
    MidwayRefreshMetricsPublisher.publishTimerMonitor('ContextWait', elapsed());
    onReady();
    return;
  }

  const deadline = performance.now() + MIDWAY_CONTEXT_POLL_TIMEOUT_MS;

  const poll = setInterval(() => {
    if (getContext()) {
      clearInterval(poll);
      MidwayRefreshMetricsPublisher.publishCounterMonitor('ContextTimeout', 0);
      MidwayRefreshMetricsPublisher.publishTimerMonitor(
        'ContextWait',
        elapsed(),
      );
      onReady();
    } else if (performance.now() >= deadline) {
      clearInterval(poll);
      logger.error(
        `Midway session refresh not started: __AACChrome__ context unavailable after ${MIDWAY_CONTEXT_POLL_TIMEOUT_MS}ms`,
      );
      MidwayRefreshMetricsPublisher.publishCounterMonitor('ContextTimeout', 1);
      MidwayRefreshMetricsPublisher.publishTimerMonitor(
        'ContextWait',
        elapsed(),
      );
    }
  }, MIDWAY_CONTEXT_POLL_INTERVAL_MS);
};

export const initMidwaySessionRefresh = () => {
  // Avoid duplicate refreshes when Chrome UI is rendered inside an iframe
  if (win.self !== win.top) return;

  whenContextReady(startMidwaySessionRefresh);
};
